import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import BackChevronIconSvg from '../../../assets/icons/icon-back-chevron.svg?react';
import IconHelpOutlineSvg from '../../../assets/icons/icon-help-outline.svg?react';
import IconInfoCircleSvg from '../../../assets/icons/icon-info-circle.svg?react';
import WithdrawalFreeWithdrawals1Svg from '../../../assets/icons/icon-withdrawal-free-withdrawals-1.svg?react';
import WithdrawalFreeWithdrawals2Svg from '../../../assets/icons/icon-withdrawal-free-withdrawals-2.svg?react';
import WithdrawalFreeWithdrawals3Svg from '../../../assets/icons/icon-withdrawal-free-withdrawals-3.svg?react';
import WithdrawalPaymentPixLogoImage from '../../../assets/logos/logo-withdrawal-payment-pix.png';
import {
  BaseButton,
  BottomNavPageLayout,
  HomeDashboardBottomNav,
  PrimaryButton,
  rubikTypographyStyles,
  UserAvatar,
} from '../../../shared/ui';

import { WithdrawalFreeWithdrawalsSheet } from './withdrawal-free-withdrawals-sheet';

type WithdrawalScreenProps = {
  avatarUrl?: string | null;
  destinationTaxId: string;
  freeWithdrawalsCount?: 1 | 2 | 3;
  onBack: () => void;
  onContinue: () => void;
  onOpenHome: () => void;
  onOpenMyPage: () => void;
  taxIdLabelKey: string;
};

const FRAME_TOP_PADDING_PX = 20;
const FRAME_SIDE_PADDING_PX = 29;
const CONTENT_TOP_GAP_PX = 14;
const CONTENT_STACK_GAP_PX = 24;
const ACTION_ROW_TOP_PADDING_PX = 15;
const ACTION_ROW_BOTTOM_PADDING_PX = 12;
const ACTION_ROW_GAP_PX = 10;
const CTA_MAX_WIDTH_PX = 196;
const FOOTER_TOP_PADDING_PX = 10;
const FOOTER_BOTTOM_PADDING_PX = 13;
const PAYMENT_METHOD_WIDTH_PX = 140;

const Screen = styled.div.attrs({
  'data-ui': 'withdrawal-screen',
})`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding-top: ${({ theme }) =>
    `max(${theme.scale.px(FRAME_TOP_PADDING_PX)}, env(safe-area-inset-top))`};
  padding-right: ${({ theme }) =>
    `max(${theme.scale.px(
      FRAME_SIDE_PADDING_PX,
    )}, env(safe-area-inset-right))`};
  padding-left: ${({ theme }) =>
    `max(${theme.scale.px(FRAME_SIDE_PADDING_PX)}, env(safe-area-inset-left))`};
`;

const Header = styled.header.attrs({
  'data-ui': 'withdrawal-header',
})`
  min-height: ${({ theme }) => theme.scale.px(56)};
  display: grid;
  grid-template-columns:
    ${({ theme }) => theme.scale.px(24)} minmax(0, 1fr)
    ${({ theme }) => theme.scale.px(39)};
  align-items: center;
  column-gap: ${({ theme }) => theme.scale.px(20)};
`;

const BackButton = styled(BaseButton)`
  width: ${({ theme }) => theme.scale.px(24)};
  height: ${({ theme }) => theme.scale.px(24)};
  display: grid;
  place-items: center;
`;

const BackIcon = styled(BackChevronIconSvg).attrs({
  'aria-hidden': true,
  focusable: 'false',
})`
  width: ${({ theme }) => theme.scale.px(12)};
  height: ${({ theme }) => theme.scale.px(20)};
  color: ${({ theme }) => theme.colors.text.primary};

  & path {
    stroke: currentColor;
  }
`;

const Title = styled.h1`
  ${rubikTypographyStyles};
  min-width: 0;
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(24)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(28)};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const SupportButton = styled(BaseButton)`
  width: ${({ theme }) => theme.scale.px(39)};
  height: ${({ theme }) => theme.scale.px(39)};
  display: grid;
  place-items: center;
`;

const SupportIcon = styled(IconHelpOutlineSvg).attrs({
  'aria-hidden': true,
  focusable: 'false',
})`
  width: 100%;
  height: 100%;
  display: block;
  --help-icon-bg: ${({ theme }) => theme.colors.support.infoSubtle};
  --help-icon-fg: ${({ theme }) => theme.colors.text.primary};
`;

const Body = styled.div.attrs({
  'data-ui': 'withdrawal-body',
})`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  display: grid;
  align-content: start;
  gap: ${({ theme }) => theme.scale.px(CONTENT_STACK_GAP_PX)};
  padding-top: ${({ theme }) => theme.scale.px(CONTENT_TOP_GAP_PX)};
`;

const PromoCard = styled.section.attrs({
  'data-ui': 'withdrawal-promo-card',
})`
  position: relative;
  min-height: ${({ theme }) => theme.scale.px(135)};
  border-radius: ${({ theme }) => theme.scale.px(10)};
  background: ${({ theme }) => theme.colors.surface.subtle};
  padding: ${({ theme }) => theme.scale.px(19)}
    ${({ theme }) => theme.scale.px(14)} ${({ theme }) => theme.scale.px(12)};
  box-sizing: border-box;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  column-gap: ${({ theme }) => theme.scale.px(14)};
  row-gap: ${({ theme }) => theme.scale.px(6)};
`;

const PromoArtworkSlot = styled.div`
  align-self: start;
  padding-top: ${({ theme }) => theme.scale.px(3)};
`;

const PromoMessage = styled.p`
  ${rubikTypographyStyles};
  margin: 0;
  white-space: pre-line;
  font-size: ${({ theme }) => theme.scale.px(16)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(19)};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const PromoInfoIcon = styled(IconInfoCircleSvg).attrs({
  'aria-hidden': true,
  focusable: 'false',
})`
  width: ${({ theme }) => theme.scale.px(14)};
  height: ${({ theme }) => theme.scale.px(14)};
  color: ${({ theme }) => theme.colors.text.primary};
  opacity: 0.5;
  --stroke-0: currentColor;
`;

const PromoInfoButton = styled(BaseButton)`
  width: ${({ theme }) => theme.scale.px(14)};
  height: ${({ theme }) => theme.scale.px(14)};
  display: grid;
  place-items: center;
  align-self: start;
`;

const PromoMeta = styled.div`
  grid-column: 2 / 4;
  display: grid;
  gap: ${({ theme }) => theme.scale.px(4)};
`;

const PromoFreeCount = styled.p`
  ${rubikTypographyStyles};
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(12)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(14)};
  color: ${({ theme }) => theme.colors.support.success};
`;

const PromoRenewalNote = styled.p`
  ${rubikTypographyStyles};
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(12)};
  line-height: ${({ theme }) => theme.scale.px(14)};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const DestinationCard = styled.section.attrs({
  'data-ui': 'withdrawal-destination-card',
})`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  column-gap: ${({ theme }) => theme.scale.px(8)};
`;

const RecipientAvatar = styled.div.attrs({
  'aria-hidden': true,
})`
  width: ${({ theme }) => theme.scale.px(41)};
  height: ${({ theme }) => theme.scale.px(41)};
`;

const DestinationText = styled.div`
  min-width: 0;
  display: grid;
  gap: ${({ theme }) => theme.scale.px(2)};
`;

const DestinationLabel = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(12)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(15)};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const DestinationTaxId = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(16)};
  font-weight: 500;
  line-height: ${({ theme }) => theme.scale.px(19)};
  color: ${({ theme }) => theme.colors.text.secondary};
  opacity: 0.5;
`;

const ActionRow = styled.div.attrs({
  'data-ui': 'withdrawal-action-row',
})`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.scale.px(ACTION_ROW_GAP_PX)};
  padding-top: ${({ theme }) => theme.scale.px(ACTION_ROW_TOP_PADDING_PX)};
  padding-bottom: ${({ theme }) =>
    theme.scale.px(ACTION_ROW_BOTTOM_PADDING_PX)};
`;

const PaymentMethodTeaser = styled.div.attrs({
  'data-ui': 'withdrawal-payment-method-teaser',
})`
  flex: 0 0 ${({ theme }) => theme.scale.px(PAYMENT_METHOD_WIDTH_PX)};
  min-width: 0;
  min-height: ${({ theme }) => theme.scale.px(56)};
  box-sizing: border-box;
  border: ${({ theme }) => theme.scale.px(1)} solid
    ${({ theme }) => theme.colors.text.primary};
  border-radius: ${({ theme }) => theme.scale.px(10)};
  padding: 0 ${({ theme }) => theme.scale.px(13)};
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  column-gap: ${({ theme }) => theme.scale.px(4)};
  background: ${({ theme }) => theme.colors.surface.base};
`;

const PaymentMethodLogo = styled.img.attrs({
  alt: '',
  'aria-hidden': true,
  src: WithdrawalPaymentPixLogoImage,
})`
  width: ${({ theme }) => theme.scale.px(35)};
  height: ${({ theme }) => theme.scale.px(35)};
  display: block;
  object-fit: contain;
`;

const PaymentMethodLabel = styled.p`
  ${rubikTypographyStyles};
  margin: 0;
  min-width: 0;
  white-space: nowrap;
  font-size: ${({ theme }) => theme.scale.px(15)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(19)};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const TransferButton = styled(PrimaryButton).attrs({
  'data-ui': 'withdrawal-transfer-button',
  type: 'button',
})`
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  max-width: ${({ theme }) => theme.scale.px(CTA_MAX_WIDTH_PX)};
  min-height: ${({ theme }) => theme.scale.px(56)};
  border-radius: ${({ theme }) => theme.scale.px(10)};

  &:disabled {
    opacity: 0.4;
  }
`;

const BottomNavSlot = styled.div.attrs({
  'data-ui': 'withdrawal-bottom-nav-slot',
})`
  flex-shrink: 0;
  display: grid;
  justify-items: center;
  padding-top: ${({ theme }) => theme.scale.px(FOOTER_TOP_PADDING_PX)};
  padding-right: ${({ theme }) =>
    `max(${theme.scale.px(
      FRAME_SIDE_PADDING_PX,
    )}, env(safe-area-inset-right))`};
  padding-bottom: ${({ theme }) =>
    `max(${theme.scale.px(
      FOOTER_BOTTOM_PADDING_PX,
    )}, env(safe-area-inset-bottom))`};
  padding-left: ${({ theme }) =>
    `max(${theme.scale.px(FRAME_SIDE_PADDING_PX)}, env(safe-area-inset-left))`};
`;

const PromoIconPlate = styled.div`
  width: ${({ theme }) => theme.scale.px(27)};
  height: ${({ theme }) => theme.scale.px(41)};
  display: block;
`;

const WithdrawalFreeWithdrawals3Icon = styled(
  WithdrawalFreeWithdrawals3Svg,
).attrs({
  'aria-hidden': true,
  focusable: 'false',
})`
  width: 100%;
  height: 100%;
  display: block;
`;

const WithdrawalFreeWithdrawals2Icon = styled(
  WithdrawalFreeWithdrawals2Svg,
).attrs({
  'aria-hidden': true,
  focusable: 'false',
})`
  width: 100%;
  height: 100%;
  display: block;
`;

const WithdrawalFreeWithdrawals1Icon = styled(
  WithdrawalFreeWithdrawals1Svg,
).attrs({
  'aria-hidden': true,
  focusable: 'false',
})`
  width: 100%;
  height: 100%;
  display: block;
`;

function WithdrawalPromoGlyph({
  freeWithdrawalsCount,
}: {
  freeWithdrawalsCount: 1 | 2 | 3;
}) {
  return (
    <PromoIconPlate>
      {freeWithdrawalsCount === 3 ? <WithdrawalFreeWithdrawals3Icon /> : null}
      {freeWithdrawalsCount === 2 ? <WithdrawalFreeWithdrawals2Icon /> : null}
      {freeWithdrawalsCount === 1 ? <WithdrawalFreeWithdrawals1Icon /> : null}
    </PromoIconPlate>
  );
}

export function WithdrawalScreen({
  avatarUrl = null,
  destinationTaxId,
  freeWithdrawalsCount = 3,
  onBack,
  onContinue,
  onOpenHome,
  onOpenMyPage,
  taxIdLabelKey,
}: WithdrawalScreenProps) {
  const { t } = useTranslation();
  const [isFreeWithdrawalsSheetOpen, setFreeWithdrawalsSheetOpen] =
    useState(false);
  const taxIdLabel = t(taxIdLabelKey);

  return (
    <BottomNavPageLayout
      frameDataUi="withdrawal-page-frame"
      overlay={
        <WithdrawalFreeWithdrawalsSheet
          isOpen={isFreeWithdrawalsSheetOpen}
          onClose={() => {
            setFreeWithdrawalsSheetOpen(false);
          }}
        />
      }
      pageDataUi="withdrawal-page"
      stickyFooter={
        <BottomNavSlot>
          <HomeDashboardBottomNav
            activeTab={null}
            onOpenHome={onOpenHome}
            onOpenMyPage={onOpenMyPage}
          />
        </BottomNavSlot>
      }
    >
      <Screen>
        <Header>
          <BackButton
            aria-label={t('withdrawal.backAria')}
            type="button"
            onClick={onBack}
          >
            <BackIcon />
          </BackButton>
          <Title>{t('withdrawal.title')}</Title>
          <SupportButton
            aria-label={t('myPage.helpAria')}
            type="button"
            onClick={() => undefined}
          >
            <SupportIcon />
          </SupportButton>
        </Header>

        <Body>
          <PromoCard>
            <PromoArtworkSlot>
              <WithdrawalPromoGlyph
                freeWithdrawalsCount={freeWithdrawalsCount}
              />
            </PromoArtworkSlot>
            <PromoMessage>{t('withdrawal.promoMessage')}</PromoMessage>
            <PromoInfoButton
              aria-label={t('withdrawal.freeWithdrawalsInfoAria')}
              type="button"
              onClick={() => {
                setFreeWithdrawalsSheetOpen(true);
              }}
            >
              <PromoInfoIcon />
            </PromoInfoButton>
            <PromoMeta>
              <PromoFreeCount>
                {t(`withdrawal.freeWithdrawals${freeWithdrawalsCount}`)}
              </PromoFreeCount>
              <PromoRenewalNote>
                {t('withdrawal.limitRenewal')}
              </PromoRenewalNote>
            </PromoMeta>
          </PromoCard>

          <DestinationCard>
            <RecipientAvatar>
              <UserAvatar avatarUrl={avatarUrl} sizePx={41} />
            </RecipientAvatar>
            <DestinationText>
              <DestinationLabel>
                {t('withdrawal.destinationLabel', { taxIdLabel })}
              </DestinationLabel>
              <DestinationTaxId>{destinationTaxId}</DestinationTaxId>
            </DestinationText>
          </DestinationCard>
        </Body>

        <ActionRow>
          <PaymentMethodTeaser>
            <PaymentMethodLogo />
            <PaymentMethodLabel>{t('withdrawal.methodPix')}</PaymentMethodLabel>
          </PaymentMethodTeaser>
          <TransferButton type="button" onClick={onContinue}>
            {t('withdrawal.continue')}
          </TransferButton>
        </ActionRow>
      </Screen>
    </BottomNavPageLayout>
  );
}
